// GitHub user-token custody (arc 101 slice 05) — the value objects for the
// user-token work lane, where a repo is driven by the USER's own GitHub account
// instead of the App installation.
//
// A user_github_tokens row is one of two KINDS, discriminated by `token_kind`:
//   - `oauth`            the App user-to-server login pair (existing, write-only
//                        login identity — `packages/github/src/user-oauth.ts`).
//   - `fine_grained_pat` a fine-grained personal access token the user connects
//                        for the work lane. Per-repo scoped, always expiring.
//
// Only fine-grained PATs are accepted for the work lane. A classic `ghp_` token
// grants blanket account access — the wrong custody blast radius for a stored
// credential — so it is rejected AT THE EDGE, never enveloped. This is a token
// FORMAT + expiry decision, not a disclosure one (see `05-gh-user-token.md`).

import { z } from 'zod';

/** The two credential kinds a `user_github_tokens` row can hold. */
export const GITHUB_USER_TOKEN_KINDS = ['oauth', 'fine_grained_pat'] as const;
export type GithubUserTokenKind = (typeof GITHUB_USER_TOKEN_KINDS)[number];
/** Text column, Zod-at-read (D3 no-pgEnum stance, same as `byok_keys.kind`). */
export const GithubUserTokenKindSchema = z.enum(GITHUB_USER_TOKEN_KINDS);

/** Fine-grained PAT prefix — the only credential the work lane accepts. */
export const FINE_GRAINED_PAT_PREFIX = 'github_pat_';
/** Classic full-access PAT prefix — rejected: too broad to hold in custody. */
export const CLASSIC_PAT_PREFIX = 'ghp_';

/**
 * Custody blast-radius cap on the REMAINING life of a connected PAT.
 *
 * It is GitHub's OWN maximum for a fine-grained PAT — 366 days — and not one day
 * less, because the value this gate sees is `expires_at` reported by GitHub. A
 * 365-day cap therefore refused the one thing it should never refuse: a token a
 * user had just minted at the maximum custom expiration and pasted into the
 * connect form seconds later, which arrives with ~366 days of remaining life and
 * answered `expiry_too_far` — "over the custody cap" — with no remedy but to wait
 * a day or re-mint shorter.
 *
 * What it still catches is what it was for: an `expires_at` GitHub could not have
 * issued (a hand-built row, a clock skew, a non-expiring credential dressed up
 * with a far-future date). Anything past what the provider can mint is not a
 * credential we hold.
 */
export const MAX_FINE_GRAINED_PAT_TTL_MS = 366 * 24 * 60 * 60 * 1000;

/** Why a presented token was refused at the connect edge. */
export type FineGrainedPatRejection =
  /** A classic `ghp_` token — blanket account scope, rejected outright. */
  | 'classic_pat'
  /** Not a `github_pat_` fine-grained token (OAuth token, gibberish, empty). */
  | 'not_fine_grained'
  /** No expiry — a non-expiring credential is never accepted for custody. */
  | 'missing_expiry'
  /** Already past its expiry at connect time. */
  | 'already_expired'
  /** Expires further out than GitHub itself can mint — over the custody cap. */
  | 'expiry_too_far';

export type FineGrainedPatCheck =
  | { readonly ok: true; readonly expiresAt: Date }
  | { readonly ok: false; readonly reason: FineGrainedPatRejection };

/**
 * Edge-validate a fine-grained PAT before it is enveloped and stored. Pure —
 * the caller (the connect route) supplies the token, GitHub's reported expiry,
 * and optionally a clock. Order matters: the classic-PAT reason is reported
 * BEFORE the generic prefix miss so the connect card can say precisely why a
 * `ghp_` token is the wrong kind, not just "not fine-grained".
 */
export function validateFineGrainedPat(input: {
  token: string;
  expiresAt: Date | null;
  now?: Date;
}): FineGrainedPatCheck {
  const token = input.token.trim();
  if (token.startsWith(CLASSIC_PAT_PREFIX)) return { ok: false, reason: 'classic_pat' };
  if (!token.startsWith(FINE_GRAINED_PAT_PREFIX)) return { ok: false, reason: 'not_fine_grained' };

  const { expiresAt } = input;
  if (expiresAt === null) return { ok: false, reason: 'missing_expiry' };

  const now = input.now ?? new Date();
  const remainingMs = expiresAt.getTime() - now.getTime();
  if (remainingMs <= 0) return { ok: false, reason: 'already_expired' };
  if (remainingMs > MAX_FINE_GRAINED_PAT_TTL_MS) return { ok: false, reason: 'expiry_too_far' };

  return { ok: true, expiresAt };
}
